import { parseInlineStyle } from "./parseInlineStyle";
import type {
  ContainerNode,
  HeadingNode,
  ImageNode,
  InfoHtmlDocument,
  InfoHtmlNode,
  ListItemNode,
  ListNode,
  ParagraphNode,
  TableCellNode,
  TableNode,
  TableRowNode,
  WinlineGridNode,
  WinlineLineSpec,
} from "./InfoHtmlNode";

/**
 * Parses an HTML string (info-page content authored on the operator CDN)
 * into the typed `InfoHtmlDocument` AST consumed by `renderInfoHtmlToDom`.
 *
 * Web-only — uses `DOMParser`.
 *
 * Inputs:
 * - `htmlString`  raw HTML fetched from the CDN (or the bundled fallback).
 * - `tokens`      `{name}` placeholders substituted in all text content.
 *                 Example: `{ min: "$0.10", max: "$100", rtp: "96.00" }`.
 * - `featureAvailability`  per-feature flags. Elements carrying a
 *                 `data-feature="..."` attribute are dropped if the flag is
 *                 explicitly `false`. Unknown flags are treated as `true`
 *                 (lenient — missing flag != disabled); `onUnpublishedGates`
 *                 names them.
 *
 * Tag allowlist:
 * - `<h1>` `<h2>` → `HeadingNode`
 * - `<p>`         → `ParagraphNode` (with `<br>` flattened to `\n`)
 * - `<img>`       → `ImageNode`  (src must start with `bundle://` or `inline://`)
 * - `<ul>`        → `ListNode`
 * - `<li>`        → `ListItemNode`
 * - `<table>`     → `TableNode`
 * - `<tr>`        → `TableRowNode`
 * - `<td>` `<th>` → `TableCellNode` (isHeader=true for `<th>`)
 * - `<div>` `<section>` → `ContainerNode`
 * - `<winlines>`  → `WinlineGridNode` (custom macro — see InfoHtmlNode.ts)
 *
 * Unknown tags are silently dropped (their text content is also dropped,
 * even when they wrap allowlisted children — keep the HTML structure tight).
 */
export interface ParseInfoHtmlInput {
  htmlString: string;
  tokens: Readonly<Record<string, string>>;
  featureAvailability: Readonly<Record<string, boolean>>;
  /**
   * Optional fall-through resolver, consulted when a `{name}` placeholder
   * isn't in the dynamic `tokens` map. Used to feed translated strings from
   * `LocalizationService.t()` into the info page — keys like `info.about.body`
   * resolve to a translated value here, then any dynamic tokens *inside that
   * value* (`{min}`, `{paytable.*}`, etc.) expand in a follow-up pass.
   *
   * Return `undefined` for unknown keys so the parser keeps the verbatim
   * `{name}` for visibility instead of swallowing the miss.
   */
  resolveToken?: (name: string) => string | undefined;
  /**
   * Called once per parse, after the walk, with the distinct `data-feature`
   * names of the WHOLE template, in document order, that are keys of no
   * `featureAvailability` entry — a name inside a subtree a false flag drops
   * included, so the report describes the template, not the settings of the
   * moment. Every such gate passes (the lenient rule above is unchanged). Not
   * called when there are none. The presenter's dev-mode report (decision 40).
   */
  onUnpublishedGates?: (names: readonly string[]) => void;
}

export function parseInfoHtml(input: ParseInfoHtmlInput): InfoHtmlDocument {
  const dom = new DOMParser().parseFromString(input.htmlString, "text/html");
  const context: ParseContext = {
    tokens: input.tokens,
    featureAvailability: input.featureAvailability,
    resolveToken: input.resolveToken,
  };

  const title = resolveTitle(dom, context);
  const children = parseChildren(dom.body, context);

  if (input.onUnpublishedGates) {
    const names = collectUnpublishedGates(dom, input.featureAvailability);
    if (names.length > 0) input.onUnpublishedGates(names);
  }

  return { title, children };
}

interface ParseContext {
  readonly tokens: Readonly<Record<string, string>>;
  readonly featureAvailability: Readonly<Record<string, boolean>>;
  readonly resolveToken?: (name: string) => string | undefined;
}

// ---------------------------------------------------------------------------
// Title resolution
// ---------------------------------------------------------------------------

function resolveTitle(dom: Document, ctx: ParseContext): string {
  const titleEl = dom.querySelector("title");
  if (titleEl?.textContent) {
    return substituteTokens(titleEl.textContent.trim(), ctx);
  }
  const firstH1 = dom.body.querySelector("h1");
  if (firstH1?.textContent) {
    return substituteTokens(firstH1.textContent.trim(), ctx);
  }
  return "";
}

// ---------------------------------------------------------------------------
// Tree walk
// ---------------------------------------------------------------------------

function parseChildren(parent: Element, ctx: ParseContext): InfoHtmlNode[] {
  const out: InfoHtmlNode[] = [];
  for (const child of Array.from(parent.children)) {
    const result = parseElement(child, ctx);
    if (result === null) continue;
    if (Array.isArray(result)) {
      out.push(...result);
    } else {
      out.push(result);
    }
  }
  return out;
}

/**
 * Returns:
 * - a single node for leaf-shaped tags
 * - an array of nodes for transparent containers (e.g. `<section>` that the
 *   parser elects to flatten — currently none, but reserved)
 * - null when the element is dropped (unknown tag, feature-gated off,
 *   malformed)
 */
function parseElement(el: Element, ctx: ParseContext): InfoHtmlNode | InfoHtmlNode[] | null {
  // Feature gating runs first — entire subtree skipped when the gate is
  // explicitly false. Done before tag dispatch so an unknown tag with a
  // disabled gate doesn't error.
  if (!passesFeatureGate(el, ctx)) return null;

  const tag = el.tagName.toLowerCase();
  switch (tag) {
    case "h1": return makeHeading(el, 1, ctx);
    case "h2": return makeHeading(el, 2, ctx);
    case "p":  return makeParagraph(el, ctx);
    case "img": return makeImage(el, ctx);
    case "ul":  return makeList(el, ctx);
    case "li":  return makeListItem(el, ctx);
    case "table": return makeTable(el, ctx);
    case "tr":    return makeTableRow(el, ctx);
    case "td":    return makeTableCell(el, false, ctx);
    case "th":    return makeTableCell(el, true,  ctx);
    case "div":     return makeContainer(el, "div", ctx);
    case "section": return makeContainer(el, "section", ctx);
    case "winlines": return makeWinlineGrid(el);
    default: return null;
  }
}

function passesFeatureGate(el: Element, ctx: ParseContext): boolean {
  const flag = el.getAttribute("data-feature");
  if (!flag) return true;
  // Lenient: only an explicit `false` blocks; missing flag passes.
  return ctx.featureAvailability[flag] !== false;
}

/**
 * The gate names no flag publishes, distinct, in document order. Walks the
 * whole DOM rather than the gated walk, so a name under a dropped subtree is
 * still named. An empty name is no gate, as in `passesFeatureGate`.
 */
function collectUnpublishedGates(dom: Document, featureAvailability: Readonly<Record<string, boolean>>): string[] {
  const names = new Set<string>();
  for (const el of Array.from(dom.body.querySelectorAll("[data-feature]"))) {
    const flag = el.getAttribute("data-feature");
    if (!flag) continue;
    if (featureAvailability[flag] === undefined) names.add(flag);
  }
  return Array.from(names);
}

// ---------------------------------------------------------------------------
// Per-tag constructors
// ---------------------------------------------------------------------------

function makeHeading(el: Element, level: 1 | 2, ctx: ParseContext): HeadingNode {
  return {
    kind: "heading",
    level,
    text: substituteTokens((el.textContent ?? "").trim(), ctx),
    style: parseInlineStyle(el.getAttribute("style")),
  };
}

function makeParagraph(el: Element, ctx: ParseContext): ParagraphNode {
  // Flatten `<br>` children into `\n` characters at parse time. The renderer
  // doesn't have to know about line-break elements.
  const text = flattenInlineText(el);
  return {
    kind: "paragraph",
    text: substituteTokens(text, ctx),
    style: parseInlineStyle(el.getAttribute("style")),
  };
}

function flattenInlineText(el: Element): string {
  const parts: string[] = [];
  el.childNodes.forEach((node) => {
    if (node.nodeType === 3 /* Text */) {
      parts.push(node.textContent ?? "");
      return;
    }
    if (node.nodeType !== 1 /* Element */) return;
    const childEl = node as Element;
    if (childEl.tagName.toLowerCase() === "br") {
      parts.push("\n");
      return;
    }
    // Other inline tags (strong/em/span/etc.) are NOT supported in v1 —
    // their text is preserved but no formatting is applied.
    parts.push(flattenInlineText(childEl));
  });
  return collapseWhitespace(parts.join(""));
}

/**
 * Collapses runs of whitespace to a single space, preserves explicit `\n`
 * line breaks (from `<br>` flattening). Trims leading/trailing whitespace.
 */
function collapseWhitespace(text: string): string {
  return text
    .replace(/[\t ]+/g, " ")
    .replace(/ *\n */g, "\n")
    .trim();
}

function makeImage(el: Element, _ctx: ParseContext): ImageNode | null {
  const src = el.getAttribute("src") ?? "";
  // Two URI schemes supported:
  //   bundle://<bundleName>/<path>[#<frame>]   — async load via the bundle
  //   inline://<atlasKey>#<frame>              — a frame of a shell-owned
  //                                              atlas (no bundle load)
  // Other schemes are dropped silently.
  if (!src.startsWith("bundle://") && !src.startsWith("inline://")) {
    return null;
  }
  const alt = el.getAttribute("alt") ?? undefined;
  return {
    kind: "image",
    src,
    alt,
    style: parseInlineStyle(el.getAttribute("style")),
  };
}

function makeList(el: Element, ctx: ParseContext): ListNode {
  const items: ListItemNode[] = [];
  for (const child of Array.from(el.children)) {
    if (child.tagName.toLowerCase() !== "li") continue;
    const result = parseElement(child, ctx);
    if (result && !Array.isArray(result) && result.kind === "list_item") {
      items.push(result);
    }
  }
  return {
    kind: "list",
    items,
    style: parseInlineStyle(el.getAttribute("style")),
  };
}

function makeListItem(el: Element, ctx: ParseContext): ListItemNode {
  return {
    kind: "list_item",
    children: parseChildrenAllowingText(el, ctx),
    style: parseInlineStyle(el.getAttribute("style")),
  };
}

function makeTable(el: Element, ctx: ParseContext): TableNode {
  const rows: TableRowNode[] = [];
  // Skip wrapper `<thead>` / `<tbody>` / `<tfoot>` — pull `<tr>`s from
  // anywhere inside the table.
  for (const tr of Array.from(el.querySelectorAll(":scope > tr, :scope > thead > tr, :scope > tbody > tr, :scope > tfoot > tr"))) {
    const result = parseElement(tr, ctx);
    if (result && !Array.isArray(result) && result.kind === "table_row") {
      rows.push(result);
    }
  }
  return {
    kind: "table",
    rows,
    style: parseInlineStyle(el.getAttribute("style")),
  };
}

function makeTableRow(el: Element, ctx: ParseContext): TableRowNode {
  const cells: TableCellNode[] = [];
  for (const child of Array.from(el.children)) {
    const childTag = child.tagName.toLowerCase();
    if (childTag !== "td" && childTag !== "th") continue;
    const result = parseElement(child, ctx);
    if (result && !Array.isArray(result) && result.kind === "table_cell") {
      cells.push(result);
    }
  }
  return {
    kind: "table_row",
    cells,
    style: parseInlineStyle(el.getAttribute("style")),
  };
}

function makeTableCell(el: Element, isHeader: boolean, ctx: ParseContext): TableCellNode {
  return {
    kind: "table_cell",
    isHeader,
    children: parseChildrenAllowingText(el, ctx),
    style: parseInlineStyle(el.getAttribute("style")),
  };
}

function makeContainer(el: Element, sourceTag: "div" | "section", ctx: ParseContext): ContainerNode {
  return {
    kind: "container",
    sourceTag,
    children: parseChildren(el, ctx),
    style: parseInlineStyle(el.getAttribute("style")),
  };
}

/**
 * `<li>` / `<td>` / `<th>` can contain either block children (other
 * allowlisted tags) OR plain text. If the cell has only text content, we
 * synthesize a `ParagraphNode` for the text so the renderer always sees a
 * uniform child list. Mixed content (text + element siblings) is supported
 * by emitting separate paragraph and child nodes.
 */
function parseChildrenAllowingText(el: Element, ctx: ParseContext): InfoHtmlNode[] {
  const hasElementChildren = el.children.length > 0;
  if (!hasElementChildren) {
    const text = substituteTokens(flattenInlineText(el), ctx);
    if (text.length === 0) return [];
    const para: ParagraphNode = { kind: "paragraph", text };
    return [para];
  }
  return parseChildren(el, ctx);
}

// ---------------------------------------------------------------------------
// Winline grid macro
// ---------------------------------------------------------------------------

function makeWinlineGrid(el: Element): WinlineGridNode | null {
  const cols = parsePositiveInt(el.getAttribute("cols"));
  const rows = parsePositiveInt(el.getAttribute("rows"));
  if (cols === null || rows === null) return null;

  const expectedCellCount = cols * rows;
  const lines: WinlineLineSpec[] = [];

  for (const lineEl of Array.from(el.children)) {
    if (lineEl.tagName.toLowerCase() !== "line") continue;
    const raw = lineEl.textContent ?? "";
    const activeCells = parseLineMask(raw, expectedCellCount);
    if (activeCells === null) continue; // skip malformed lines silently
    lines.push({ activeCells });
  }

  return {
    kind: "winline_grid",
    cols,
    rows,
    lines,
    style: parseInlineStyle(el.getAttribute("style")),
  };
}

function parseLineMask(raw: string, expectedLength: number): boolean[] | null {
  const cleaned: boolean[] = [];
  for (const ch of raw) {
    if (ch === "0") cleaned.push(false);
    else if (ch === "1") cleaned.push(true);
    // anything else (whitespace, commas, etc.) is ignored
  }
  if (cleaned.length !== expectedLength) return null;
  return cleaned;
}

function parsePositiveInt(value: string | null): number | null {
  if (!value) return null;
  const num = Number.parseInt(value, 10);
  if (!Number.isFinite(num) || num <= 0) return null;
  return num;
}

// ---------------------------------------------------------------------------
// Token substitution
// ---------------------------------------------------------------------------

const TOKEN_PATTERN = /\{([a-zA-Z_][a-zA-Z0-9_.]*)\}/g;

// Bound on recursive substitution depth. i18n values may themselves contain
// dynamic tokens (e.g. `info.bet.body` → "Minimum bet: {min}..."), so one
// expansion isn't enough. Cap depth so a self-referencing key in JSON can't
// blow the stack.
const MAX_SUBSTITUTION_DEPTH = 4;

/**
 * Replaces `{name}` placeholders with values from (1) the dynamic tokens
 * map, then (2) the optional `resolveToken` fall-through (used for i18n
 * strings). The resolved value is itself fed back through substitution so
 * that an i18n string containing `{min}` or `{paytable.*}` expands those
 * dynamic tokens too. Unknown tokens are left verbatim — surfacing missing
 * data as a visible "{xxx}" in the UI is more diagnosable than silently
 * emitting empty strings.
 */
function substituteTokens(text: string, ctx: ParseContext, depth: number = 0): string {
  if (depth >= MAX_SUBSTITUTION_DEPTH) return text;
  return text.replace(TOKEN_PATTERN, (match, name) => {
    const dynamic = ctx.tokens[name];
    if (dynamic !== undefined) {
      return substituteTokens(dynamic, ctx, depth + 1);
    }
    const resolved = ctx.resolveToken?.(name);
    if (resolved !== undefined) {
      return substituteTokens(resolved, ctx, depth + 1);
    }
    return match;
  });
}
